import { useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { QueryConstraint, QueryDocumentSnapshot } from "firebase/firestore";
import type { PaginationProps } from "@/components/ui/pagination";
import { fetchPage, type WithId } from "./use-firestore";
import { DEFAULT_PAGE_SIZE } from "./use-pagination";

export interface ServerPagination<T> {
  rows: T[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Verdadeiro enquanto uma página nova chega, com a anterior ainda na tela. */
  isFetching: boolean;
  paginationProps: Omit<PaginationProps, "itemLabel" | "className">;
}

/**
 * Paginação no servidor, por cursor, para as listas de histórico.
 *
 * Pedidos e notificações crescem sem limite, e carregar o período inteiro para
 * mostrar vinte e cinco linhas era o que tornava essas telas lentas. Aqui cada
 * página é uma consulta com `limit`, e a seguinte começa depois do último
 * documento da anterior (`startAfter`).
 *
 * Os cursores ficam guardados por página, para voltar não exigir refazer o
 * caminho desde o início. Mudar `key` (filtro, período) descarta os cursores e
 * volta à primeira página.
 *
 * `constraints` precisa terminar num `orderBy` de campo presente em todo
 * documento; ver `fetchPage`.
 */
export function usePagedQuery<T extends WithId>({
  key,
  path,
  constraints,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
  total,
}: {
  key: readonly unknown[];
  path: string;
  constraints: QueryConstraint[];
  pageSize?: number;
  enabled?: boolean;
  /** Total conhecido por agregação, quando a tela o tem. */
  total?: number;
}): ServerPagination<T> {
  const keyText = JSON.stringify(key);
  const [state, setState] = useState({ page: 0, key: keyText });
  // Cursores por filtro: os de outro filtro apontam para outra consulta, e um
  // filtro novo começa sem nenhum. A ref só é lida e escrita fora do render.
  const cursorsByKey = useRef(new Map<string, (QueryDocumentSnapshot | null)[]>());

  // Filtro novo volta à primeira página.
  const page = state.key === keyText ? state.page : 0;
  if (state.key !== keyText) {
    setState({ page: 0, key: keyText });
  }

  const query = useQuery({
    queryKey: [...key, "page", page, pageSize],
    queryFn: () =>
      fetchPage<T>(
        path,
        constraints,
        pageSize,
        cursorsByKey.current.get(keyText)?.[page] ?? null,
      ),
    enabled,
    // Mantém a página anterior na tela enquanto a próxima chega: sem isso a
    // tabela pisca vazia a cada clique em "Próxima".
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.rows ?? [];
  const rangeStart = rows.length ? page * pageSize + 1 : 0;
  const rangeEnd = page * pageSize + rows.length;

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    paginationProps: {
      page: page + 1,
      pageCount: total != null ? Math.max(1, Math.ceil(total / pageSize)) : undefined,
      total,
      rangeStart,
      rangeEnd,
      hasPrevious: page > 0,
      hasNext: Boolean(query.data?.hasMore) && !query.isPlaceholderData,
      onPrevious: () => setState({ page: Math.max(0, page - 1), key: keyText }),
      onNext: () => {
        // O cursor da próxima página sai dos dados da atual, e não de dentro
        // da consulta: uma página que veio do cache não roda a consulta, e o
        // cursor nunca seria gravado.
        const cursors = cursorsByKey.current.get(keyText) ?? [null];
        cursors[page + 1] = query.data?.cursor ?? null;
        // Só o filtro atual fica guardado.
        cursorsByKey.current = new Map([[keyText, cursors]]);
        setState({ page: page + 1, key: keyText });
      },
      loading: query.isFetching,
    },
  };
}
